import type {
  ProgramPriorityItem,
  SearchFilters,
  ProgramSearchResults,
  ProgramDetails,
  ProgramSearchResult
} from '../../types/rpc-contracts';
import { callRpc, createRpcError } from './base';
import { ensureArray, ensureObject } from './util';

type ProgramPriorityListParams = {
  minCategories?: number;
  organization?: string | null;
  limit?: number;
};

const validateSearchFilters = (filters: SearchFilters): void => {
  if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
    throw createRpcError('INVALID_LIMIT', 'Search limit must be between 1 and 1000', {
      limit: filters.limit
    });
  }

  if (filters.offset && filters.offset < 0) {
    throw createRpcError('INVALID_OFFSET', 'Search offset must be non-negative', {
      offset: filters.offset
    });
  }

  if (
    filters.quality_score_min &&
    (filters.quality_score_min < 0 || filters.quality_score_min > 100)
  ) {
    throw createRpcError(
      'INVALID_QUALITY_SCORE',
      'Quality score minimum must be between 0 and 100',
      { quality_score_min: filters.quality_score_min }
    );
  }

  if (filters.foundation_year_range) {
    const { min, max } = filters.foundation_year_range;
    if (typeof min === 'number' && typeof max === 'number' && min > max) {
      throw createRpcError(
        'INVALID_YEAR_RANGE',
        'Foundation year minimum cannot be greater than maximum',
        { foundation_year_range: filters.foundation_year_range }
      );
    }
  }
};

export const getProgramsPriorityList = async (
  params: ProgramPriorityListParams = {}
): Promise<ProgramPriorityItem[]> => {
  const { minCategories = 1, organization = null, limit = 50 } = params;

  return await callRpc('get_programs_priority_list', {
    params: {
      p_min_categories: minCategories,
      p_organization: organization,
      p_limit: limit
    },
    transform: (data) =>
      ensureArray<ProgramPriorityItem>(
        data,
        'INVALID_RESPONSE_FORMAT',
        'Priority list response is not an array',
        { data }
      ),
    successMeta: (result) => ({ resultCount: result.length })
  });
};

export const searchProgramsAdvanced = async (
  filters: SearchFilters = {}
): Promise<ProgramSearchResults> => {
  validateSearchFilters(filters);

  return await callRpc('search_programs_v2', {
    params: { filters },
    transform: (data) => {
      const payload = ensureObject<ProgramSearchResults & { error?: unknown }>(
        data,
        'INVALID_SEARCH_RESPONSE',
        'Search returned invalid response format',
        { data, filters }
      );

      if (payload.error) {
        throw createRpcError(
          'SEARCH_PROGRAMS_ADVANCED_FAILED',
          'Program search failed. Please retry or contact an administrator.',
          { filters, error: payload.error }
        );
      }

      return payload;
    },
    successMeta: (result) => ({ resultCount: result.programs?.length ?? 0 })
  });
};

export const getProgramsExtended = async (
  filters: SearchFilters = {}
): Promise<ProgramSearchResults> => {
  validateSearchFilters(filters);

  return await callRpc('get_programs_extended', {
    params: {
      p_limit: filters.limit ?? 50,
      p_offset: filters.offset ?? 0,
      p_search: filters.text_search ?? null,
      p_include_quality_scores: true
    },
    transform: (data) =>
      ensureObject<ProgramSearchResults>(
        data,
        'INVALID_EXTENDED_RESPONSE',
        'Extended programs returned invalid response format',
        { data, filters }
      ),
    successMeta: (result) => ({ resultCount: result.programs?.length ?? 0 })
  });
};

export const searchProgramsSemantic = async (
  embedding: number[],
  filters: Omit<SearchFilters, 'text_search' | 'search_mode'> = {}
): Promise<ProgramSearchResults> => {
  return await callRpc('search_programs_semantic', {
    params: {
      p_search_embedding: JSON.stringify(embedding),
      p_limit: filters.limit ?? 50,
      p_isbn_required: filters.isbn_required ?? null,
      p_independent_publishers_allowed: filters.independent_publishers_allowed ?? null,
      p_multiple_prizes_offered: filters.multiple_prizes_offered ?? null,
      p_entry_fee_required: filters.entry_fee_required ?? null,
      p_has_past_winners_url: filters.has_past_winners_url ?? null,
      p_category_count_min: filters.category_count_min ?? null
    },
    transform: (data) => {
      const programs = ensureArray<ProgramSearchResult>(
        data,
        'INVALID_SEARCH_RESPONSE',
        'Semantic search returned invalid response',
        {
          data,
          filters
        }
      );
      const totalCategoryCount = programs.reduce((sum, program) => sum + (program.category_count ?? 0), 0);
      const totalWinOpportunities = programs.reduce((sum, program) => {
        // Only count explicit win_opportunities (don't fall back to categories)
        return sum + (program.win_opportunities ?? 0);
      }, 0);
      return {
        programs,
        total_count: Array.isArray(data) ? data.length : 0,
        total_category_count: totalCategoryCount,
        total_win_opportunities: totalWinOpportunities,
        applied_filters: { ...filters, search_mode: 'semantic' },
        query_performance: {
          execution_time_ms: 0,
          cache_hit: false
        }
      };
    },
    successMeta: (result, durationMs) => {
      result.query_performance.execution_time_ms = durationMs;
      return { resultCount: result.programs.length };
    }
  });
};

export const searchProgramsHybrid = async (
  text: string,
  embedding: number[],
  ftsWeight: number = 0.6,
  semanticWeight: number = 0.4,
  filters: Omit<SearchFilters, 'search_mode'> = {}
): Promise<ProgramSearchResults> => {
  return await callRpc('search_programs_hybrid', {
    params: {
      p_search_term: text,
      p_search_embedding: JSON.stringify(embedding),
      p_fts_weight: ftsWeight,
      p_semantic_weight: semanticWeight,
      p_limit: filters.limit ?? 50,
      p_isbn_required: filters.isbn_required ?? null,
      p_independent_publishers_allowed: filters.independent_publishers_allowed ?? null,
      p_multiple_prizes_offered: filters.multiple_prizes_offered ?? null,
      p_entry_fee_required: filters.entry_fee_required ?? null,
      p_has_past_winners_url: filters.has_past_winners_url ?? null,
      p_category_count_min: filters.category_count_min ?? null
    },
    transform: (data) => {
      const programs = ensureArray<ProgramSearchResult>(
        data,
        'INVALID_SEARCH_RESPONSE',
        'Hybrid search returned invalid response',
        { data, filters, text }
      );
      const totalCategoryCount = programs.reduce((sum, program) => sum + (program.category_count ?? 0), 0);
      const totalWinOpportunities = programs.reduce((sum, program) => {
        // Only count explicit win_opportunities (don't fall back to categories)
        return sum + (program.win_opportunities ?? 0);
      }, 0);
      return {
        programs,
        total_count: Array.isArray(data) ? data.length : 0,
        total_category_count: totalCategoryCount,
        total_win_opportunities: totalWinOpportunities,
        applied_filters: { ...filters, text_search: text, search_mode: 'hybrid' },
        query_performance: {
          execution_time_ms: 0,
          cache_hit: false
        }
      };
    },
    successMeta: (result, durationMs) => {
      result.query_performance.execution_time_ms = durationMs;
      return { resultCount: result.programs.length };
    }
  });
};

export const getProgramDetails = async (
  programId: string
): Promise<ProgramDetails> => {
  return await callRpc('get_program_details', {
    params: { program_id: programId },
    transform: (data) =>
      ensureObject<ProgramDetails>(
        data,
        'INVALID_PROGRAM_DETAILS',
        'Program details returned invalid response',
        { data, programId }
      )
  });
};

export const getProgramCardByCode = async (
  programCode: string
): Promise<unknown> => {
  return await callRpc('get_program_card_by_code', {
    params: { program_code: programCode }
  });
};
